import { useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import { PLACEHOLDER_COLOR } from '@/components/@IntranetLibrary/DatePicker/styles';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildCreditNoteHtml,
  CreditNoteSnapshot,
  type CreditNoteTarget,
} from '@/components/Approval/@components/CreditNote';
import SearchableSelect, {
  type SearchableSelectOption,
} from '@/components/Approval/@components/SearchableSelect';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getCreditNoteDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildCreditNoteEditorContent,
  buildCreditNoteLabel,
  buildCreditNoteSubject,
  CREDIT_NOTE_BLOCKED_MESSAGE,
  pickCreditNoteCard,
  type CreditNoteCandidate,
  type CreditNoteCard,
  type CreditNoteDraftSource,
  type CreditNoteItem,
} from '@/utils/approval/creditNote';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean; editorApi: ReplaceContentApi | null };

/**
 * GERP CREDIT NOTE(156) — 내 미상신 건을 여러 개 체크하면 건마다 카드를 불러옴.
 * 고르거나 뺄 때마다 제목·본문을 덮어씀. 편집은 건을 바꿀 수 없고 저장 카드만
 */
const CreditNoteField = ({ isEditMode, editorApi }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const saved = useWatch({ control, name: 'creditNote' });
  const addedModalRef = useRef<ModalRef>(null);

  /** 고른 순서 그대로 */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [cardsById, setCardsById] = useState<Record<string, CreditNoteCard>>(
    {},
  );
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  // 카드 응답(비동기)이 최신 선택·카드를 보도록 state 와 같이 둠
  const selectedRef = useRef<string[]>([]);
  const cardsRef = useRef<Record<string, CreditNoteCard>>({});

  const { data: source, isLoading } = useSWR<CreditNoteDraftSource>(
    isEditMode ? null : getCreditNoteDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  const candidateById = useMemo(
    () =>
      new Map<string, CreditNoteCandidate>(
        (source?.display.candidates ?? []).map((c) => [c.aprvId, c]),
      ),
    [source],
  );

  const labelOf = (id: string) => {
    const c = candidateById.get(id);
    return c ? buildCreditNoteLabel(c) : id;
  };

  const toItems = (
    ids: string[],
    loaded: Record<string, CreditNoteCard>,
  ): CreditNoteItem[] =>
    ids.flatMap((id) =>
      loaded[id]
        ? [
            {
              aprvId: id,
              label: labelOf(id),
              aprvRev: candidateById.get(id)?.aprvRev?.trim() ?? '',
              card: loaded[id],
            },
          ]
        : [],
    );

  /** 카드를 다 받았을 때만 저장 값 — 덜 받았으면 상신 시 안내 */
  const syncSaved = (ids: string[], loaded: Record<string, CreditNoteCard>) => {
    const first = candidateById.get(ids[0] ?? '');
    const isReady = !!first && ids.every((id) => loaded[id]);
    setValue(
      'creditNote',
      isReady
        ? {
            systemId: first.sysId,
            approvalTypeCode: first.aprvTypcd,
            approvalId: ids.join(','),
            content: buildCreditNoteHtml(toItems(ids, loaded)),
          }
        : null,
      { shouldDirty: true },
    );
  };

  /** 레거시 change_title_content — 고르거나 뺄 때마다 제목·본문 덮어씀 */
  const overwriteSubjectAndBody = (ids: string[]) => {
    const labels = ids.map(labelOf);
    setValue('subject', buildCreditNoteSubject(labels, t), {
      shouldDirty: true,
    });
    editorApi?.replace(buildCreditNoteEditorContent(labels, t));
  };

  const update = (ids: string[], loaded: Record<string, CreditNoteCard>) => {
    selectedRef.current = ids;
    cardsRef.current = loaded;
    setSelectedIds(ids);
    setCardsById(loaded);
  };

  // 레거시 pre_cost_yn — 체크한 건을 불러온 뒤에 제목·본문에 넣음
  const loadCard = async (id: string) => {
    setLoadingIds((prev) => [...prev, id]);
    try {
      const data = await getNestedDataFetcher<CreditNoteDraftSource>(
        getCreditNoteDraftSourceUrl([id]),
      );
      const card = pickCreditNoteCard(data, id);
      if (!card) throw new Error('card missing');
      // 응답 전에 체크를 풀었으면 버림
      if (!selectedRef.current.includes(id)) return;
      const next = { ...cardsRef.current, [id]: card };
      update(selectedRef.current, next);
      overwriteSubjectAndBody(selectedRef.current.filter((k) => next[k]));
      syncSaved(selectedRef.current, next);
    } catch (error) {
      if (!selectedRef.current.includes(id)) return;
      // 레거시처럼 체크 해제 — 같은 건을 다시 골라 재시도 가능
      const next = selectedRef.current.filter((k) => k !== id);
      update(next, cardsRef.current);
      syncSaved(next, cardsRef.current);
      const isBlocked =
        axios.isAxiosError(error) && error.response?.status === 422;
      snackbar.showError({
        message: t(
          isBlocked
            ? CREDIT_NOTE_BLOCKED_MESSAGE
            : '데이터를 불러오지 못했습니다.',
        ),
      });
    } finally {
      setLoadingIds((prev) => prev.filter((k) => k !== id));
    }
  };

  const applySelection = (nextIds: string[]) => {
    const added = nextIds.filter((id) => !selectedRef.current.includes(id));
    const removed = selectedRef.current.filter((id) => !nextIds.includes(id));
    const loaded = { ...cardsRef.current };
    removed.forEach((id) => delete loaded[id]);
    update(nextIds, loaded);
    // 추가는 카드를 받은 뒤(loadCard), 빼기는 바로
    if (removed.length)
      overwriteSubjectAndBody(nextIds.filter((id) => loaded[id]));
    syncSaved(nextIds, loaded);
    added.forEach(loadCard);
  };

  // 레거시 DelCost — 확인 없이 바로 뺌
  const handleDelete = ({ itemId }: CreditNoteTarget) =>
    applySelection(selectedRef.current.filter((id) => id !== itemId));

  if (isEditMode)
    return (
      <FieldBlock>
        {saved?.content && <CreditNoteSnapshot content={saved.content} />}
      </FieldBlock>
    );

  const options: SearchableSelectOption[] = candidates.map((c) => {
    const label = buildCreditNoteLabel(c);
    return {
      value: c.aprvId,
      label,
      labelNode: (
        <OptionLabel>
          <Checkbox
            size="small"
            checked={selectedIds.includes(c.aprvId)}
            tabIndex={-1}
            sx={{ p: 0, pointerEvents: 'none' }}
          />
          {label}
        </OptionLabel>
      ),
    };
  });
  const placeholder = t('결재할 건을 선택해주세요');
  const items = toItems(selectedIds, cardsById);

  return (
    <FieldBlock>
      <ControlRow>
        <SearchableSelect
          multiple
          value={selectedIds}
          options={options}
          placeholder={isLoading ? t('불러오는 중...') : placeholder}
          noOptionsText={isLoading ? null : t('진행할 내역이 없습니다.')}
          // 레거시 콤보처럼 고른 건 대신 안내 문구 — 고른 건은 아래 카드
          renderValue={() => <Placeholder>{placeholder}</Placeholder>}
          onChange={applySelection}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        {loadingIds.length > 0 && <Spinner theme="black" size="16px" />}
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          onClick={() => addedModalRef.current?.open()}
        >
          {t('추가 내역 확인')}
        </BoxButton>
      </ControlRow>
      {items.length > 0 && (
        <CreditNoteSnapshot
          content={buildCreditNoteHtml(items)}
          onDelete={handleDelete}
        />
      )}
      <Modal
        ref={addedModalRef}
        title={t('추가 내역 확인')}
        size="x-small"
        handleClose={() => addedModalRef.current?.close()}
      >
        <AddedList>
          {items.map(({ aprvId, label }) => (
            <Body2 key={aprvId} component="li">
              {label}
            </Body2>
          ))}
        </AddedList>
      </Modal>
    </FieldBlock>
  );
};

const ControlRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  [theme.breakpoints.down('md')]: {
    flexWrap: 'wrap',
    '& > .MuiInputBase-root, & > .MuiFormControl-root': { flexBasis: '100%' },
  },
}));

const OptionLabel = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const Placeholder = styled('span')({ color: PLACEHOLDER_COLOR });

const AddedList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
  '& > li + li': { marginTop: 4 },
});

export default CreditNoteField;
